import { Box, Button } from '@mui/material';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { parseAmountInput, parseHoursInput, useContractEditing, useFinancials } from './editor';
import { useGuidedEntry } from './flow';
import { PaymentSchedule } from './PaymentSchedule';
import { RevenueDistribution } from './RevenueDistribution';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { InlineTextCell } from '@/components/planning/cells';
import { MICRO_LABEL_TRACKING } from '@/components/ui';
import {
  formatAmount,
  formatAmountAbbrev,
  caretAfterDigits,
  digitsBefore,
  groupDigitsWhileTyping,
} from '@/lib';

/**
 * The totals strip: the total amount (inline edit; the "≈ N hrs work" line under
 * it went on 2026-09-29) and, beside it, the revenue-distribution bar
 * (`RevenueDistribution`). The budgeted cost (CALC-12) and the budgeted factor
 * with its trend moved to the aside's Contract summary on 2026-09-29; the bar
 * went with them and came back on 2026-10-14.
 *
 * Whether the reading is a factor or a margin comes from the budget-model
 * config (ADR 0027) — there is deliberately no in-product toggle.
 *
 * On an empty contract the amount / hours step renders here as the design's
 * ringed input. There is no longer a launchpad button in front of it: the walk
 * starts itself (`GuidedEntryProvider`), because the page has exactly one thing
 * to ask for first and the field's own placeholder already says what it is.
 * Escape leaves the walk and falls through to the ordinary click-to-edit total
 * below, which is the way back in.
 */
export const TotalsRow = () => {
  const { state, dispatch } = useFinancials();
  const { isEditing, startEditing, stopEditing, advanceFrom } = useContractEditing();
  const guided = useGuidedEntry();
  const flowInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);
  /*
    Once-only per typed value, like InlineTextCell's `committedRef`: Enter, Tab,
    Escape and Next each settle the step, and the blur that follows them must
    not commit again — by then the walk is on the NEXT step, and a second
    commit would land this value there. Typing re-arms it.
  */
  const flowSettledRef = useRef(false);
  const commitFlowEntry = () => {
    const input = flowInputRef.current;
    if (!input || flowSettledRef.current) return;
    flowSettledRef.current = true;
    guided.commit(
      flowEntryStep === 'amount' ? parseAmountInput(input.value) : parseHoursInput(input.value),
    );
  };

  const { revenue } = state.document;
  const flowEntryStep =
    guided.flow && (guided.flow.step === 'amount' || guided.flow.step === 'hours')
      ? guided.flow.step
      : null;
  const flowPlaceholder =
    flowEntryStep === 'amount' ? t('totals.PlaceholderAmount') : t('totals.PlaceholderHours');

  return (
    <Box
      data-automation-id="totals-row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 2,
        rowGap: 2,
        pb: 2,
        mb: 0.5,
      }}
    >
      {/* amount / guided entry */}
      <Box sx={{ flex: '3 1 220px', minWidth: 0 }}>
        {flowEntryStep ? (
          <Box data-automation-id="flow-entry">
            <Box
              sx={{
                fontSize: 11,
                letterSpacing: MICRO_LABEL_TRACKING,
                textTransform: 'uppercase',
                color: 'var(--fg-2)',
                fontWeight: 500,
                mb: 1,
              }}
            >
              {flowEntryStep === 'amount' ? t('totals.LabelAmount') : t('totals.LabelOwnHours')}
            </Box>
            <Box
              sx={{
                // Hugs the placeholder (see the input's width) rather than the column.
                display: 'inline-flex',
                maxWidth: '100%',
                alignItems: 'center',
                border: 'var(--border-width-thick) solid var(--color-pink-80)',
                borderRadius: 'var(--border-radius-small)',
                bgcolor: 'var(--bg-panel)',
                overflow: 'hidden',
                height: 38,
                boxShadow: '0 0 0 3px var(--color-pink-10)',
              }}
            >
              <Box
                component="input"
                ref={flowInputRef}
                autoFocus
                data-automation-id="flow-entry-input"
                placeholder={flowPlaceholder}
                inputMode="numeric"
                /*
                  Uncontrolled, so the grouping is written straight onto the
                  element and the caret set in the same tick — no React state to
                  race. Same rule as the other amount fields: digits group, the
                  `1,2m` shorthand is left alone for `parseAmountInput`.
                */
                onChange={(event) => {
                  const input = event.currentTarget as HTMLInputElement;
                  flowSettledRef.current = false;
                  const grouped = groupDigitsWhileTyping(input.value);
                  if (grouped === input.value) return;
                  const digits = digitsBefore(input.value, input.selectionStart ?? 0);
                  input.value = grouped;
                  const position = caretAfterDigits(grouped, digits);
                  input.setSelectionRange(position, position);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === 'Tab') {
                    event.preventDefault();
                    commitFlowEntry();
                  } else if (event.key === 'Escape') {
                    event.preventDefault();
                    flowSettledRef.current = true;
                    guided.exit();
                  }
                }}
                /*
                  Leaving the field confirms it (pilot testing, 2026-10-12):
                  people typed the amount and never pressed Enter, so nothing
                  was seeded. Every other field on the page already commits on
                  blur, the walk's later steps included. An empty or unparsable
                  value is still a no-op, so clicking around an untouched
                  contract keeps its walk.
                */
                onBlur={commitFlowEntry}
                onFocus={() => {
                  flowSettledRef.current = false;
                }}
                sx={{
                  // Mono, as every other amount field on the contract is: this
                  // was the one input still on the proportional face, so its
                  // digits did not line up with the totals it feeds.
                  fontFamily: 'var(--font-family-mono)',
                  bgcolor: 'transparent',
                  px: 1.5,
                  // The face is mono, so `ch` is exact: the field is its placeholder's width.
                  width: `calc(${String(flowPlaceholder.length)}ch + var(--spacing-unit-3))`,
                  minWidth: 0,
                  border: 'none',
                  outline: 'none',
                  fontSize: 14,
                }}
              />
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 500,
                  color: 'var(--fg-3)',
                  px: 1,
                  mr: 1,
                  letterSpacing: MICRO_LABEL_TRACKING,
                }}
              >
                {flowEntryStep === 'amount' ? state.document.currency : t('totals.HoursUnit')}
              </Box>
              {/*
                The visible way on, for anyone who does not reach for Enter.
                Filled secondary at the tiny height, so it sits inside the
                ringed field rather than filling it (pilot annotation, 2026-10-12).
                Mouse-down is swallowed so the input keeps focus — otherwise its
                blur would commit first and the click would land on the walk's
                next step.
              */}
              <Button
                variant="contained"
                color="secondary"
                size="tiny"
                data-automation-id="flow-entry-next"
                onMouseDown={(event) => event.preventDefault()}
                onClick={commitFlowEntry}
                endIcon={<MaterialSymbol name="arrow_forward" size={16} />}
                sx={{ mr: 0.5, flex: 'none' }}
              >
                {t('totals.Next')}
              </Button>
            </Box>
            <Box sx={{ fontSize: 11, color: 'var(--fg-3)', mt: 1, lineHeight: 1.35 }}>
              {flowEntryStep === 'amount' ? t('totals.HintAmount') : t('totals.HintHours')}
            </Box>
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                fontSize: 11,
                letterSpacing: MICRO_LABEL_TRACKING,
                textTransform: 'uppercase',
                color: 'var(--fg-3)',
                mb: 0.5,
              }}
            >
              {t('pmCommon:term.TotalAmount')}
            </Box>
            <InlineTextCell
              automationId="contract-revenue"
              formatWhileTyping={groupDigitsWhileTyping}
              value={revenue > 0 ? formatAmount(revenue) : ''}
              editing={isEditing('revenue')}
              onStartEdit={() => startEditing('revenue')}
              onCancel={stopEditing}
              advanceOnEnter
              onCommit={(raw, intent) => {
                const amount = parseAmountInput(raw);
                if (amount !== null) dispatch({ type: 'SET_REVENUE', revenue: amount });
                advanceFrom('revenue', intent);
              }}
              inputWidth={160}
              inputSx={{ fontFamily: 'var(--font-family-mono)', fontSize: 20, fontWeight: 500 }}
              display={
                <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 20,
                      fontWeight: 500,
                      color: 'var(--fg-heading)',
                      lineHeight: 1,
                    }}
                  >
                    {formatAmountAbbrev(revenue)}
                  </Box>
                  <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
                    {state.document.currency}
                  </Box>
                </Box>
              }
            />
            {/* The action under the amount, fixed price only (BR-31); it renders nothing otherwise. */}
            <PaymentSchedule />
          </Box>
        )}
      </Box>

      {/*
        Back beside the total amount (designer's ask, 2026-10-14) after two
        weeks at the top of the aside's Contract summary — which now lands
        collapsed, so the bar would have been out of sight by default.
      */}
      <Box sx={{ flex: '5 1 240px', minWidth: 0, px: 2 }}>
        <RevenueDistribution />
      </Box>
    </Box>
  );
};
